import { create } from 'zustand';
import { SETTABLE_STATUSES } from '../utils/presenceStatus';
import { pushStatus } from '../services/userPresenceService';

/**
 * This device's user-set presence status (online / away / busy).
 *
 * Deliberately its OWN store with its OWN localStorage key rather than a field
 * on `preferencesStore` / `appPreferencesStore`: those two modules are owned by
 * the update-cadence task right now, and this value is per DEVICE by design
 * anyway — Phoenix Presence keys metas per connection (every `Presence.track`
 * in the backend carries a `client_id`), so "busy on my laptop, online on my
 * phone" is the shape the presence layer already has.
 *
 * Since 27.08.2026 (row 38, Stage 2) the status IS pushed to the server, via
 * `userPresenceService.pushStatus` — the one module allowed to speak the
 * presence protocol. localStorage keeps being written anyway, and that is not
 * belt-and-braces: a device-local status must survive a socket that is down,
 * and the free (local-only) plan never has a socket at all. The push is a
 * best-effort side effect of the local write, never a precondition for it.
 *
 * `SETTABLE_STATUSES`, not `STATUSES`: `offline` is renderable but not
 * settable. It is what the SERVER says about somebody nobody is connected as;
 * the user's own way to look offline is `invisible`, and the server answers
 * `invalid_status` to a pushed `"offline"`.
 */

export const USER_STATUS_KEY = 'apiboo-user-status';
export const DEFAULT_USER_STATUS = 'online';

function load() {
  try {
    const raw = localStorage.getItem(USER_STATUS_KEY);
    return SETTABLE_STATUSES.includes(raw) ? raw : DEFAULT_USER_STATUS;
  } catch {
    // Private mode / storage disabled: a status is a nicety, not a failure.
    return DEFAULT_USER_STATUS;
  }
}

export const useUserStatusStore = create((set) => ({
  status: load(),
  setStatus: (status) => {
    const next = SETTABLE_STATUSES.includes(status) ? status : DEFAULT_USER_STATUS;
    try {
      localStorage.setItem(USER_STATUS_KEY, next);
    } catch {
      /* ignore — keep the in-memory value */
    }
    set({ status: next });
    // After the local write, never instead of it.
    pushStatus(next);
  },
}));
